import React, { FC, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { fetchData } from '../../helpers/apiUtils';
import { DnvLeadStatRow } from '../../helpers/dnvAssetAnalysisData';

const DnvStatsPanel: FC = () => {
  const [rows, setRows] = useState<DnvLeadStatRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const result = await fetchData<DnvLeadStatRow[]>('/api/dnv-asset-analysis/stats');
      if (cancelled) return;
      if (result.success && result.data) {
        setRows(result.data);
      } else {
        setError(result.error || 'Fehler beim Laden der Statistik');
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;

  const totals = rows.reduce(
    (acc, r) => ({ leads: acc.leads + r.leads, turnover: acc.turnover + r.turnover_eur }),
    { leads: 0, turnover: 0 },
  );

  return (
    <Box>
      <Typography variant="h6" mb={2}>
        Bisherige Kampagnen — Lead-Aggregation pro adref-Prefix
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={2}>
        Affiliate <code>DNV_Asset_Analysis_SAM</code>, lead_status=<code>new</code>. Pricing:
        Override-Werte (MSM=10€, FT=3€, BG=3€, FRP=10€), sonst Fallback auf{' '}
        <code>lead_prices</code>. Gruppierung delimiter-basiert vor{' '}
        <code>:Customer_ID_</code>.
      </Typography>
      <TableContainer
        component={Paper}
        sx={{ backgroundColor: '#ffffff', maxHeight: 700 }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' }}
              >
                Meta-Infos (adref Prefix)
              </TableCell>
              <TableCell
                align="right"
                sx={{ backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' }}
              >
                Leads
              </TableCell>
              <TableCell
                align="right"
                sx={{ backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' }}
              >
                Abrechnungswert
              </TableCell>
              <TableCell
                sx={{ backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' }}
              >
                Details
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Keine Daten vorhanden.
                </TableCell>
              </TableRow>
            ) : (
              <>
                <TableRow sx={{ backgroundColor: '#fafafa' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Insgesamt</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {totals.leads.toLocaleString('de-DE')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {totals.turnover.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{' '}
                    €
                  </TableCell>
                  <TableCell />
                </TableRow>
                {rows.map((r) => (
                  <TableRow key={r.meta} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.meta}</TableCell>
                    <TableCell align="right">{r.leads.toLocaleString('de-DE')}</TableCell>
                    <TableCell align="right">
                      {r.turnover_eur.toLocaleString('de-DE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      €
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{r.details}</TableCell>
                  </TableRow>
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DnvStatsPanel;
